import React from 'react';
import { Box, Button, Card, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';

const DOT_STYLES = {
  dailyWork: {
    step1: { className: 'dot-step1', bgcolor: 'error.main' },
    step12: { className: 'dot-step12', bgcolor: 'warning.main' },
    all: { className: 'dot-all', bgcolor: 'success.main' },
  },
  mapping: {
    unmapped: { className: 'dot-unmapped', bgcolor: 'error.main' },
    mapped: { className: 'dot-mapped', bgcolor: 'success.main' },
  },
};

function DailyWorkMonthCalendar({
  date,
  viewMonth,
  onSelectDate,
  onViewMonthChange,
  dateEntryStatus,
  mode = 'dailyWork',
  legendKey,
}) {
  const { t } = useTranslation();
  const monthNames = t('dates.months', { returnObjects: true });
  const weekDays = t('dates.weekdaysShort', { returnObjects: true });
  const [y, m] = viewMonth.split('-').map(Number);
  const year = y;
  const month = m - 1;
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startPad = firstDay.getDay();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);
  while (cells.length < 42) cells.push(null);

  const toDateStr = (day) => (day == null ? '' : `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
  const isSelected = (day) => day != null && date === toDateStr(day);
  const status = (day) => (day != null && dateEntryStatus[toDateStr(day)]) || null;
  const dotStyles = DOT_STYLES[mode] || DOT_STYLES.dailyWork;

  const getAriaLabel = (day, dateStr, entryStatus) => {
    if (!entryStatus) return `Day ${day}, ${dateStr}`;
    if (mode === 'mapping') {
      return `Day ${day}, ${dateStr}, ${entryStatus === 'mapped' ? 'mapped' : 'not mapped'}`;
    }
    if (entryStatus === 'all') return `Day ${day}, ${dateStr}, all steps saved`;
    if (entryStatus === 'step12') return `Day ${day}, ${dateStr}, step 2 only`;
    return `Day ${day}, ${dateStr}, step 1 only`;
  };

  const dotSx = Object.fromEntries(
    Object.entries(dotStyles).map(([key, value]) => [
      `& .${value.className}`,
      {
        position: 'absolute',
        bottom: 2,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 6,
        height: 6,
        borderRadius: '50%',
        bgcolor: value.bgcolor,
      },
    ])
  );

  const monthLabel = Array.isArray(monthNames) ? monthNames[month] : monthNames;

  return (
    <Card
      variant="outlined"
      sx={{ overflow: 'hidden' }}
      role="application"
      aria-label={t('dates.calendarAria', { month: monthLabel, year })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <IconButton onClick={() => onViewMonthChange(-1)} aria-label={t('myHours.previousMonth')} size="small">
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="subtitle1" component="h3" id="calendar-month-label">
          {monthLabel} {year}
        </Typography>
        <IconButton onClick={() => onViewMonthChange(1)} aria-label={t('myHours.nextMonth')} size="small">
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          p: 1,
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.5,
          justifyItems: 'center',
        }}
        aria-labelledby="calendar-month-label"
        role="grid"
        aria-readonly="true"
      >
        {(Array.isArray(weekDays) ? weekDays : []).map((wd) => (
          <Typography key={wd} variant="caption" color="text.secondary" component="span" sx={{ py: 0.5 }}>
            {wd}
          </Typography>
        ))}
        {cells.map((day, idx) => {
          if (day == null) {
            return <Box key={`empty-${idx}`} sx={{ minHeight: 36 }} />;
          }
          const dateStr = toDateStr(day);
          const selected = isSelected(day);
          const entryStatus = status(day);
          const dotStyle = entryStatus ? dotStyles[entryStatus] : null;
          return (
            <Box key={`${day}-${idx}`} sx={{ minHeight: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Button
                variant={selected ? 'contained' : 'text'}
                size="small"
                onClick={() => onSelectDate(dateStr)}
                aria-label={getAriaLabel(day, dateStr, entryStatus)}
                sx={{
                  minWidth: 32,
                  height: 32,
                  position: 'relative',
                  ...dotSx,
                }}
              >
                {day}
                {dotStyle && <span className={dotStyle.className} aria-hidden="true" />}
              </Button>
            </Box>
          );
        })}
      </Box>
      {Object.keys(dateEntryStatus).length > 0 && legendKey && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, pb: 1 }} aria-hidden="true">
          {t(legendKey)}
        </Typography>
      )}
    </Card>
  );
}

export default DailyWorkMonthCalendar;
